import { useEffect, useState } from 'react';

import { listVersions, type CmsVersion, type OwnerRef } from '../../lib/cmsAdmin';
import { ApiError } from '../../lib/apiClient';

/**
 * Every version, and the way back to one.
 *
 * ── ROLLBACK MAKES A NEW DRAFT; IT NEVER REVIVES A ROW ───────────────────────────────
 *
 * Restoring an old version copies its document into the current draft. The old version
 * stays exactly as it was, superseded, and the restored content goes live only when
 * somebody publishes it — through the same validation as any other publish.
 *
 * Flipping an old row back to `published` would be less code and quietly wrong: history
 * would no longer be a record of what was live and when, two rows could claim the same
 * state, and a version published before a block changed could go live again without
 * passing today's rules.
 */
export function VersionHistory({
  owner, currentDraftVersion, onRestore, onClose,
}: {
  owner: OwnerRef;
  currentDraftVersion: number | null;
  onRestore: (version: CmsVersion) => void;
  onClose: () => void;
}) {
  const [versions, setVersions] = useState<CmsVersion[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listVersions(owner)
      .then((rows) => { if (!cancelled) setVersions(rows); })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'The history could not be loaded.');
        }
      });
    return () => { cancelled = true; };
  }, [owner]);

  return (
    <div className="cms-modal" role="dialog" aria-modal="true" aria-label="Version history">
      <div className="cms-modal__panel cms-modal__panel--wide">
        <header className="cms-modal__head">
          <h2>Version history</h2>
          <button type="button" className="button button--ghost adm-btn-sm" onClick={onClose}>Close</button>
        </header>

        {error && <p className="alert alert--error" role="alert">{error}</p>}
        {!versions && !error && <p className="adm-muted">Loading…</p>}

        {versions && (
          <table className="adm-table cms-versions">
            <thead>
              <tr>
                <th>Version</th><th>State</th><th>Saved</th><th>By</th><th>Published</th><th />
              </tr>
            </thead>
            <tbody>
              {versions.map((v) => (
                <tr key={v.id}>
                  <td>v{v.version}</td>
                  <td><StateBadge state={v.state} /></td>
                  <td>{new Date(v.createdAt).toLocaleString()}</td>
                  <td>{v.createdBy ?? '—'}</td>
                  <td>{v.publishedAt ? new Date(v.publishedAt).toLocaleString() : '—'}</td>
                  <td>
                    {v.version !== currentDraftVersion && (
                      <button type="button" className="button button--ghost adm-btn-sm"
                        onClick={() => onRestore(v)}>
                        Restore into draft
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <p className="adm-hint">
          Restoring copies that version into the current draft. Nothing changes for
          visitors until the draft is published.
        </p>
      </div>
    </div>
  );
}

function StateBadge({ state }: { state: CmsVersion['state'] }) {
  if (state === 'published') return <span className="badge badge--ok">Live</span>;
  if (state === 'draft') return <span className="badge badge--pending">Draft</span>;
  return <span className="badge">{state}</span>;
}
